import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    filterFns,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable
} from "@tanstack/react-table";
import classnames from "classnames";
import _ from "lodash";
import {Pagination} from "../ReactTable/Pagination";
import {ReactTableHeader} from "../ReactTable/ReactTableHeader";
import {ReactTableBody} from "../ReactTable/ReactTableBody";
import {DefaultColumnFilter} from "./ReactTable/helpers";
import {wrapValueLabelInOptionStructure} from "../../utils/maybeAddLabelToOptions";
import PropTypes from "prop-types";
import {handleScrollChange} from "./ReactTable/scrollLogic";
import {getJsonParamOrDefault, updateQueryStringForState} from "../../utils/urlBuilder";
import {ReactTableFooter} from "../ReactTable/ReactTableFooter";
import {faIcons} from "../../utils/iconConstants";


const ReactTable = (props) => {
    const {
        data,
        columns,
        initialState,
        children,
        className = "",
        loading = false,
        showPagination = true,
        trClassName = "",
        getTrProps = () => ({}),
        filterable = false,
        hasStickyHeader = false,
        getTdProps = () => {},
        reactTableXCoordinateRef = undefined,
        updatePageIndex = () => undefined,
        updatePageSize = () => undefined,
        saveSortStateToUrl = false,
        savePageIndexToUrl = false,
        getRowId,
        autoResetSelectedRows = true,
        showPageSizeDropdown = true,
        meta
    } = props;

    const memoColumns = useMemo(() => {
        return columns.map(x => ({...x, filterFn: x.filterFn || filterFns.includesString}))
            .filter(col => _.has(col, "show") ? col.show : true);
    }, [columns]);

    const defaultColumn = React.useMemo(() => ({filter: DefaultColumnFilter, minWidth: 30, maxWidth: 400}), []);

    const stateProps = {}
    const onChangeProps = {}

    const [sorting, setSorting] = useState(saveSortStateToUrl ? getJsonParamOrDefault('sorting', []) : undefined)

    if (saveSortStateToUrl) {
        stateProps.sorting = sorting
        onChangeProps.onSortingChange = setSorting
    }

    useEffect(() => {
        if (!saveSortStateToUrl) return;
        updateQueryStringForState({sorting: JSON.stringify(sorting)})
    }, [saveSortStateToUrl, sorting])

    const [pagination, setPagination] = useState(savePageIndexToUrl ? {
        ...(initialState?.pagination || {}),
        pageIndex: getJsonParamOrDefault('pageIndex', initialState?.pagination?.pageIndex) || 0,
    } : undefined)

    if (savePageIndexToUrl) {
        stateProps.pagination = pagination
        onChangeProps.onPaginationChange = setPagination
    }

    useEffect(() => {
        if (!savePageIndexToUrl) return;
        updateQueryStringForState({pageIndex: pagination?.pageIndex})
    }, [savePageIndexToUrl, pagination])

    const [columnFilters, setColumnFilters] = useState(initialState?.columnFilters || [])
    stateProps.columnFilters = columnFilters
    onChangeProps.onColumnFiltersChange = setColumnFilters

    const [rowSelection, setRowSelection] = useState({})
    stateProps.rowSelection = rowSelection
    onChangeProps.onRowSelectionChange = setRowSelection

    const table = useReactTable(
        {
            columns: memoColumns, data, defaultColumn,
            initialState: initialState,
            meta,
            autoResetSelectedRows,
            getRowId,
            state: {
                ...stateProps,
                columnFilters
            },
            ...onChangeProps,
            getFilteredRowModel: getFilteredRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            getCoreRowModel: getCoreRowModel(),
            getSortedRowModel: getSortedRowModel(),
            enableColumnResizing: true,
            columnResizeMode: "onChange",
            autoResetPageIndex: false
        }
    );


    const updateTableXCoordinate = event => {
        if (reactTableXCoordinateRef) {
            reactTableXCoordinateRef.current = event.currentTarget.scrollLeft;
        }
    };

    const state = table.getState();
    const {pagination: {pageIndex, pageSize}} = state;
    const stickyClassName = hasStickyHeader ? "sticky" : "";
    const stickyHeaderClassName = hasStickyHeader ? "headerForStickyHeader" : "";
    const stickyBodyClassName = hasStickyHeader ? "bodyForStickyHeader" : "";

    React.useLayoutEffect(() => {
        if (!_.isEmpty(reactTableXCoordinateRef)) {
            const rtTable = document.getElementsByClassName("rt-table")[0];
            if (rtTable)
                rtTable.scrollLeft = reactTableXCoordinateRef.current;
        }
    }, []);


    const updatePageIndexRef = useRef(updatePageIndex);
    updatePageIndexRef.current = updatePageIndex;
    const updatePageSizeRef = useRef(updatePageSize);
    updatePageSizeRef.current = updatePageSize;

    useEffect(() => {
        updatePageIndexRef.current(pageIndex);
        updatePageSizeRef.current(pageSize);
    }, [pageIndex, pageSize]);

    useEffect(() => {
        if (!hasStickyHeader) return;
        return handleScrollChange();
    }, [hasStickyHeader, pageIndex, pageSize]);


    const hasFooterRow = () => {
        const footerRows = table
            .getFooterGroups()
            .map((group) => group.headers.map((header) => header.column.columnDef.footer))
            .flat()
            .filter(Boolean);
        return footerRows.length > 0
    }

    const isFirstColumnFiltersRender = React.useRef(true);
    useEffect(() => {
        if (isFirstColumnFiltersRender.current) {
            isFirstColumnFiltersRender.current = false;
            return;
        }
        if (autoResetSelectedRows && !_.isEmpty(table.getState().rowSelection)) {
            table.resetRowSelection();
        }
    }, [columnFilters, autoResetSelectedRows])

    return <>
        <div className={"react-table-wrapper"} data-testid={"react-table-wrapper"}>
            {children && children({rows: table.getFilteredRowModel().rows, table})}
            <div className={classnames(className, stickyClassName, "ReactTable7")} data-testid={"ReactTable7"}>
                <div className={"rt-table"} data-testid={'rt-table'}
                     data-testref={reactTableXCoordinateRef}
                     onScroll={updateTableXCoordinate}>
                    <div className={classnames(stickyHeaderClassName, "rt-thead -header")}
                         data-testid={"ReactTable7-rt-thead"}>
                        {
                            table.getHeaderGroups().map((headerGroup, i) => <ReactTableHeader
                                key={`headerGroup_${i}`}
                                headerGroup={headerGroup}
                                table={table}
                                filterable={filterable}/>)
                        }
                    </div>
                    <div className={classnames(stickyBodyClassName, "rt-tbody")}
                         data-testid={"ReactTable7-rt-tbody"}>
                        <div role={"rowgroup"} className="rt-tr-group" data-testid={"react-table-body"}>
                            {
                                table.getRowModel().rows.map((row, ind) => <ReactTableBody
                                    key={row.id}
                                    row={row} index={ind}
                                    table={table}
                                    trClassName={trClassName}
                                    getTrProps={getTrProps}
                                    getTdProps={getTdProps}/>)
                            }
                        </div>
                    </div>
                    {hasFooterRow() && <div role={"rowgroup"} className="rt-tr-group rt-rt-footer-group"
                                            data-testid={"react-table-footer"}>
                        {
                            table.getFooterGroups().map((row, ind) => {
                                return <ReactTableFooter
                                    key={`footerGroup_${ind}`}
                                    row={row} index={ind}
                                    table={table}/>;
                            })
                        }
                    </div>}
                </div>
                {showPagination && (
                    <Pagination
                        showPageSizeDropdown={showPageSizeDropdown}
                        firstPageOnClick={() => table.setPageIndex(0)}
                        canPreviousPage={table.getCanPreviousPage()}
                        previousPageOnClick={() => table.previousPage()}
                        pageIndex={pageIndex}
                        pageOptions={table.getPageOptions()}
                        value={pageSize}
                        onChange={(option) => table.setPageSize(Number(option.value))}
                        dropdownCallBack={(pageSize) => wrapValueLabelInOptionStructure(pageSize, `Show ${pageSize}`)}
                        nextPageOnClick={() => table.nextPage()}
                        canNextPage={table.getCanNextPage()}
                        lastPageOnClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    />)
                }


                <div className={classnames("-loading", loading ? "-active" : "")}>
                    {
                        loading && <div className="-loading-inner" data-testid={"loading-spinner"}>
                            <i className={`${faIcons.carrot} fa-spin spinner`}/> Loading...
                        </div>
                    }
                </div>
            </div>
        </div>
    </>;
};

export default ReactTable;

const paginationShape = {
    pageSize: PropTypes.number,
    pageIndex: PropTypes.number
};

const initialState = {
    columnFilters: PropTypes.arrayOf(PropTypes.object),
    sorting: PropTypes.arrayOf(PropTypes.object),
    pagination: PropTypes.shape(paginationShape)
};

ReactTable.propTypes = {
    data: PropTypes.array,
    columns: PropTypes.array,
    initialState: PropTypes.shape(initialState),
    children: PropTypes.any,
    className: PropTypes.string,
    loading: PropTypes.bool,
    showPagination: PropTypes.bool,
    trClassName: PropTypes.string,
    getTrProps: PropTypes.func,
    filterable: PropTypes.bool,
    hasStickyHeader: PropTypes.bool,
    getRowId: PropTypes.func,
    getTdProps: PropTypes.func,
    showPageSizeDropdown: PropTypes.bool,
    reactTableXCoordinateRef: PropTypes.object,
    savePageIndexToUrl: PropTypes.bool,
    saveSortStateToUrl: PropTypes.bool,
    autoResetSelectedRows: PropTypes.bool,
    meta: PropTypes.object
}
